import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  LinearProgress,
  Paper,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { KeyboardArrowLeft, KeyboardArrowRight, Edit as EditIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import PageLayout from '@/components/layout/frames/PageLayout';
import { useExperimentStore } from '@/store/experimentStore';
import { getExperimentMeasurements } from '@/services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Experiment } from '@/types/experiment';
import { useAuthStore } from '@/store/authStore';
import { useUnauthorizedAction } from '../../App';
import Plot from 'react-plotly.js';
import ExperimentEditDialog from '@/components/dialogs/ExperimentEditDialog';

// Helper for query params
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Table columns definition
const columns = [
  { id: 'exp_id', label: 'EXP ID' },
  { id: 'raspi_id', label: 'RASPI ID' },
  { id: 'exp_status', label: 'EXP STATUS' },
  { id: 'test_id', label: 'TEST ID' },
  { id: 'start_time', label: 'START TIME' },
  { id: 'end_time', label: 'END TIME' },
  { id: 'monitoring', label: 'MONITORING' },
];

function formatDate(date: Date | null): React.ReactNode {
  if (!date) return '';
  const localDate = new Date(date);
  if (isNaN(localDate.getTime())) return '';
  
  // Format: DD-MMM on first line, HH:MM:SS on second line
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(localDate.getDate()).padStart(2, '0');
  const month = monthNames[localDate.getMonth()];
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');
  const seconds = String(localDate.getSeconds()).padStart(2, '0');
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
      <div>{day}-{month}</div>
      <div>{hours}:{minutes}:{seconds}</div>
    </Box>
  );
}

// Helper to generate step line and measurement points for a profile
// Repeats the profile num_profiles times
function generateProfilePlotData({ steps, values, num_profiles, measurement_intervals, exp_points_counter, exp_points_total }: {
  steps: number[];
  values: number[];
  num_profiles: number;
  measurement_intervals: number;
  exp_points_counter: number;
  exp_points_total: number;
}) {
  // Calculate cycle duration (sum of all time steps)
  const cycleDuration = steps.reduce((sum, step) => sum + step, 0);
  
  // Validate num_profiles (default to 1 if invalid)
  const validNumProfiles = num_profiles > 0 ? num_profiles : 1;
  
  // Validate measurement_intervals (default to 1 if invalid)
  const validMeasurementIntervals = measurement_intervals > 0 ? measurement_intervals : 1;
  
  // Build the step line for all cycles
  const stepLineX = [0];
  const stepLineY = [values[0] ?? null];
  
  // Build measurement points for all cycles
  const measurementX = [];
  const measurementY = [];
  
  // Repeat profile for each cycle
  for (let cycle = 0; cycle < validNumProfiles; cycle++) {
    const cycleOffset = cycle * cycleDuration;
    let time = cycleOffset;
    
    // Build steps for this cycle
    for (let i = 0; i < steps.length; i++) {
      // Add step line point
      time += steps[i];
      stepLineX.push(time);
      stepLineY.push(values[i + 1] ?? values[i]);
      
      // Add measurement points for this step
      for (let j = 0; j < validMeasurementIntervals; j++) {
        const dt = steps[i] * (j / validMeasurementIntervals);
        measurementX.push(time - steps[i] + dt); // Start of step + offset
        measurementY.push(values[i]); // Same value for all points in this step
      }
    }
  }
  
  // Only show up to exp_points_total
  const expectedX = measurementX.slice(0, exp_points_total);
  const expectedY = measurementY.slice(0, exp_points_total);
  
  // For actual measured points (if exp_points_counter < exp_points_total)
  const measuredX = measurementX.slice(0, exp_points_counter);
  const measuredY = measurementY.slice(0, exp_points_counter);

  return {
    stepLine: { x: stepLineX, y: stepLineY },
    measurement: { x: expectedX, y: expectedY },
    measured: { x: measuredX, y: measuredY },
  };
}

const Experiments = () => {
  const { allExperiments, fetchAllExperiments, isLoading } = useExperimentStore();
  
  // Create stable reference for fetch function to prevent infinite re-renders
  const fetchAllExperimentsRef = useRef(fetchAllExperiments);
  
  const [orderBy, setOrderBy] = useState('exp_id');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedExpId, setSelectedExpId] = useState<string | null>(null);
  const navigate = useNavigate();
  const query = useQuery();
  const [isDetailsFrameExpanded, setIsDetailsFrameExpanded] = useState(false);

  // Edit experiment dialog state
  const [experimentDialogOpen, setExperimentDialogOpen] = useState(false);
  const [experimentDialogExpId, setExperimentDialogExpId] = useState<string | null>(null);

  // Load experiments on mount
  useEffect(() => {
    fetchAllExperimentsRef.current();
  }, []);

  // Handle exp_id from query param
  useEffect(() => {
    const expIdParam = query.get('exp_id');
    if (expIdParam && allExperiments[expIdParam]) {
      setSelectedExpId(expIdParam);
    }
  }, [query, allExperiments]);

  // Prepare rows
  const rows = useMemo(() => Object.values(allExperiments), [allExperiments]);

  // --- Checklist filter state ---
  const [checklistFilters, setChecklistFilters] = useState<{ [key: string]: string[] }>({
    raspi_id: [],
    exp_status: [],
  });
  // For menu open/close
  const [anchorEls, setAnchorEls] = useState<{ [key: string]: null | HTMLElement }>({
    raspi_id: null,
    exp_status: null,
  });

  // Get unique values for raspi_id and exp_status
  const uniqueRaspiIds = useMemo(() => {
    const vals = new Set<string>();
    rows.forEach(row => {
      if (typeof row.raspi_id === 'string' && row.raspi_id) vals.add(row.raspi_id);
    });
    return Array.from(vals).sort();
  }, [rows]);
  const uniqueExpStatuses = useMemo(() => {
    const vals = new Set<string>();
    rows.forEach(row => {
      if (typeof row.exp_status === 'string' && row.exp_status) vals.add(row.exp_status);
    });
    return Array.from(vals).sort();
  }, [rows]);

  // --- Checklist filter handlers ---
  const handleChecklistMenuOpen = (colId: string, event: React.MouseEvent<HTMLElement>) => {
    setAnchorEls(prev => ({ ...prev, [colId]: event.currentTarget }));
  };
  const handleChecklistMenuClose = (colId: string) => {
    setAnchorEls(prev => ({ ...prev, [colId]: null }));
  };
  const handleChecklistToggle = (colId: string, value: string) => {
    if (typeof value !== 'string' || !value) return; // type guard for safety
    setChecklistFilters(prev => {
      const current = Array.isArray(prev[colId]) ? prev[colId].filter((v): v is string => typeof v === 'string' && !!v) : [];
      if (current.includes(value)) {
        return { ...prev, [colId]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [colId]: [...current, value] };
      }
    });
    setPage(0);
  };
  const handleChecklistClear = (colId: string) => {
    setChecklistFilters(prev => ({ ...prev, [colId]: [] }));
    setPage(0);
  };

  // Filtering
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Checklist filters for raspi_id and exp_status
      if (
        checklistFilters.raspi_id.length > 0 &&
        !checklistFilters.raspi_id.includes(row.raspi_id ?? '')
      ) {
        return false;
      }
      if (
        checklistFilters.exp_status.length > 0 &&
        !checklistFilters.exp_status.includes(row.exp_status ?? '')
      ) {
        return false;
      }
      // No other filters
      return true;
    });
  }, [rows, checklistFilters]);

  // Sorting
  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      let aValue = a[orderBy as keyof Experiment];
      let bValue = b[orderBy as keyof Experiment];
      if (orderBy === 'monitoring') {
        aValue = (a.exp_points_total || 0) === 0 ? 0 : (a.exp_points_counter || 0) / (a.exp_points_total || 1);
        bValue = (b.exp_points_total || 0) === 0 ? 0 : (b.exp_points_counter || 0) / (b.exp_points_total || 1);
      }
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      if (aValue instanceof Date && bValue instanceof Date) {
        return order === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return order === 'asc'
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });
  }, [filteredRows, orderBy, order]);

  // Pagination
  const paginatedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  // Handle sort
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle row click
  const handleRowClick = (exp_id: string) => {
    setSelectedExpId(exp_id);
    navigate(`/experiments?exp_id=${exp_id}`);
  };

  // Authorization check
  const getRole = useAuthStore((state) => state.getRole);
  const isViewer = getRole() === 'viewer' || getRole() === 'public';
  const triggerUnauthorized = useUnauthorizedAction();

  // Details frame content
  const selectedExperiment = selectedExpId ? allExperiments[selectedExpId] : null;
  // Local state for exp_notes editing
  const [expNotes, setExpNotes] = useState('');
  const [notesChanged, setNotesChanged] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  useEffect(() => {
    if (selectedExperiment) {
      setExpNotes(selectedExperiment.exp_notes || '');
      setNotesChanged(false);
    }
  }, [selectedExperiment]);

  // Real measured control-sensor series (time/pressure/temperature) for the
  // graph, read from the experiment CSV via the hub. The profile-derived
  // "measured" points were wrong (just the expected values cut to the counter),
  // so we fetch the actual readings and plot those in green instead. Re-fetches
  // as the counter grows so a running experiment updates live.
  const [measuredReal, setMeasuredReal] = useState<{ time: number[]; pressure: (number | null)[]; temperature: (number | null)[] }>({ time: [], pressure: [], temperature: [] });
  useEffect(() => {
    let cancelled = false;
    if (!selectedExpId) { setMeasuredReal({ time: [], pressure: [], temperature: [] }); return; }
    getExperimentMeasurements(selectedExpId)
      .then((res) => { if (!cancelled && res) setMeasuredReal({ time: res.time || [], pressure: res.pressure || [], temperature: res.temperature || [] }); })
      .catch(() => { if (!cancelled) setMeasuredReal({ time: [], pressure: [], temperature: [] }); });
    return () => { cancelled = true; };
  }, [selectedExpId, selectedExperiment?.exp_points_counter]);
  const handleSaveNotes = async () => {
    if (!selectedExperiment) return;
    if (isViewer) {
      triggerUnauthorized('You are not authorized to edit experiment notes.');
      return;
    }
    setSavingNotes(true);
    await useExperimentStore.getState().updateExperimentFromUser(selectedExperiment.exp_id, { exp_notes: expNotes });
    setNotesChanged(false);
    setSavingNotes(false);
  };
  // Use database values for total time and points
  const totalTime = useMemo(() => {
    if (!selectedExperiment) return 0;
    return selectedExperiment.exp_time_total || 0;
  }, [selectedExperiment]);
  // Time unit state: 's' for seconds, 'h' for hours
  const [timeUnit, setTimeUnit] = useState<'s' | 'h'>('s');
  // Profile series visibility state
  const [showProfileSeries, setShowProfileSeries] = useState(true);
  const [showMeasuredPoints, setShowMeasuredPoints] = useState(true);
  const [showExpectedPoints, setShowExpectedPoints] = useState(true);
  // Helper to convert time array based on unit
  function convertTimeArray(arr: number[], unit: 's' | 'h') {
    return unit === 'h' ? arr.map((v) => v / 3600) : arr;
  }
  // Helper to get x-axis title
  function getXAxisTitle(unit: 's' | 'h') {
    return unit === 'h' ? 'Time [h]' : 'Time [s]';
  }
  const expDetailsFrame = (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflow: 'auto' }}>
      {/* Row 1: Header row with expand/collapse and title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => setIsDetailsFrameExpanded(exp => !exp)}
          size="small"
          sx={{ mr: 1 }}
        >
          {isDetailsFrameExpanded ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Experiment {selectedExperiment ? selectedExperiment.exp_id : '—'}
        </Typography>
        {selectedExperiment && (
          <IconButton
            size="small"
            onClick={isViewer ? () => triggerUnauthorized() : () => {
              setExperimentDialogExpId(selectedExperiment.exp_id);
              setExperimentDialogOpen(true);
            }}
            sx={{ 
              color: 'primary.main',
              '&:hover': { 
                backgroundColor: 'primary.light',
                color: 'white'
              }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {/* Row 2: Experiment notes */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            label="Experiment Notes"
            value={expNotes}
            onChange={e => { setExpNotes(e.target.value); setNotesChanged(true); }}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
          />
          <IconButton
            color="primary"
            onClick={handleSaveNotes}
            disabled={!notesChanged || savingNotes}
            sx={{ ml: 1 }}
          >
            <SaveIcon />
          </IconButton>
        </Box>
      </Box>
      {/* Row 3: Exp data table (2 rows) */}
      <TableContainer component={Paper} sx={{ maxWidth: 600 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Profile ID</TableCell>
              <TableCell>Meas Intervals</TableCell>
              <TableCell>Num Profiles</TableCell>
              <TableCell>Total Time</TableCell>
              <TableCell>Total Points</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{selectedExperiment?.profile_id || '—'}</TableCell>
              <TableCell>{selectedExperiment?.measurement_intervals || '—'}</TableCell>
              <TableCell>{selectedExperiment?.num_profiles || '—'}</TableCell>
              <TableCell>{totalTime}</TableCell>
              <TableCell>{selectedExperiment?.exp_points_total ?? '—'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {/* Rows 4-6: Plot area with border */}
      <Paper variant="outlined" sx={{ p: 2, mt: 1, mb: 1, borderColor: 'primary.main', background: '#fafbfc', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Row 4: Series legend toggles */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Checkbox
              checked={showProfileSeries}
              onChange={e => setShowProfileSeries(e.target.checked)}
              color="primary"
              size="small"
            />
            <Typography variant="body2">Profile Line</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Checkbox
              checked={showMeasuredPoints}
              onChange={e => setShowMeasuredPoints(e.target.checked)}
              color="primary"
              size="small"
            />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'red' }} />
            <Typography variant="body2">Measured Points</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Checkbox
              checked={showExpectedPoints}
              onChange={e => setShowExpectedPoints(e.target.checked)}
              color="primary"
              size="small"
            />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid red', bgcolor: 'transparent' }} />
            <Typography variant="body2">Expected Points</Typography>
          </Box>
        </Box>
        {/* Row 5: Plots (vertical layout) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Pressure plot */}
          <Plot
            data={(() => {
              const d = generateProfilePlotData({
                steps: selectedExperiment?.time_steps || [],
                values: selectedExperiment?.pressure_steps || [],
                num_profiles: selectedExperiment?.num_profiles || 1,
                measurement_intervals: selectedExperiment?.measurement_intervals || 0,
                exp_points_counter: selectedExperiment?.exp_points_counter || 0,
                exp_points_total: selectedExperiment?.exp_points_total || 0,
              });
              return [
                showProfileSeries ? {
                  x: convertTimeArray(d.stepLine.x, timeUnit),
                  y: d.stepLine.y,
                  type: 'scatter',
                  mode: 'lines',
                  line: { shape: 'hv', color: '#1976d2', width: 2 },
                  marker: { color: '#1976d2' },
                  name: `Profile ${selectedExperiment?.profile_id}`,
                  showlegend: false,
                } : null,
                showProfileSeries && showMeasuredPoints ? {
                  // Real measured control-sensor pressure from the experiment CSV.
                  x: convertTimeArray(measuredReal.time, timeUnit),
                  y: measuredReal.pressure,
                  type: 'scatter',
                  mode: 'markers',
                  marker: { color: '#2e7d32', size: 8, symbol: 'circle' },
                  name: 'Measured Points',
                  showlegend: false,
                } : null,
                showExpectedPoints ? {
                  x: convertTimeArray(d.measurement.x, timeUnit),
                  y: d.measurement.y,
                  type: 'scatter',
                  mode: 'markers',
                  marker: { color: 'red', size: 6, symbol: 'circle-open' },
                  name: 'Expected Points',
                  showlegend: false,
                } : null,
              ].filter(Boolean);
            })()}
            layout={{
              autosize: true,
              height: 180,
              margin: { l: 50, r: 20, t: 30, b: 40 },
              xaxis: {
                title: '',
                showline: true,
                showgrid: true,
                zeroline: true,
                linecolor: '#333',
                linewidth: 2,
                mirror: true,
              },
              yaxis: {
                title: { text: 'Pressure [mmHg]', font: { color: '#222', size: 10 } },
                showline: true,
                showgrid: true,
                zeroline: true,
                linecolor: '#333',
                linewidth: 2,
                mirror: true,
              },
              title: 'Pressure Profile',
              plot_bgcolor: 'white',
              paper_bgcolor: 'white',
              showlegend: false,
            }}
            useResizeHandler={true}
            style={{ width: '100%', height: '100%' }}
            config={{ displayModeBar: false }}
          />
          {/* Temperature plot */}
          <Plot
            data={(() => {
              const d = generateProfilePlotData({
                steps: selectedExperiment?.time_steps || [],
                values: selectedExperiment?.temperature_steps || [],
                num_profiles: selectedExperiment?.num_profiles || 1,
                measurement_intervals: selectedExperiment?.measurement_intervals || 0,
                exp_points_counter: selectedExperiment?.exp_points_counter || 0,
                exp_points_total: selectedExperiment?.exp_points_total || 0,
              });
              return [
                showProfileSeries ? {
                  x: convertTimeArray(d.stepLine.x, timeUnit),
                  y: d.stepLine.y,
                  type: 'scatter',
                  mode: 'lines',
                  line: { shape: 'hv', color: '#1976d2', width: 2 },
                  marker: { color: '#1976d2' },
                  name: `Profile ${selectedExperiment?.profile_id}`,
                  showlegend: false,
                } : null,
                showProfileSeries && showMeasuredPoints ? {
                  // Real measured control-sensor temperature from the experiment CSV.
                  x: convertTimeArray(measuredReal.time, timeUnit),
                  y: measuredReal.temperature,
                  type: 'scatter',
                  mode: 'markers',
                  marker: { color: '#2e7d32', size: 8, symbol: 'circle' },
                  name: 'Measured Points',
                  showlegend: false,
                } : null,
                showExpectedPoints ? {
                  x: convertTimeArray(d.measurement.x, timeUnit),
                  y: d.measurement.y,
                  type: 'scatter',
                  mode: 'markers',
                  marker: { color: 'red', size: 6, symbol: 'circle-open' },
                  name: 'Expected Points',
                  showlegend: false,
                } : null,
              ].filter(Boolean);
            })()}
            layout={{
              autosize: true,
              height: 180,
              margin: { l: 50, r: 20, t: 30, b: 40 },
              xaxis: {
                title: '',
                showline: true,
                showgrid: true,
                zeroline: true,
                linecolor: '#333',
                linewidth: 2,
                mirror: true,
              },
              yaxis: {
                title: { text: 'Temperature [°C]', font: { color: '#222', size: 10 } },
                showline: true,
                showgrid: true,
                zeroline: true,
                linecolor: '#333',
                linewidth: 2,
                mirror: true,
              },
              title: 'Temperature Profile',
              plot_bgcolor: 'white',
              paper_bgcolor: 'white',
              showlegend: false,
            }}
            useResizeHandler={true}
            style={{ width: '100%', height: '100%' }}
            config={{ displayModeBar: false }}
          />
        </Box>
        {/* Row 6: X-axis title and time unit toggle */}
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1, gap: 2, borderTop: '1px solid #e0e0e0', pt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 2 }}>
            {getXAxisTitle(timeUnit)}
          </Typography>
          <FormControl component="fieldset" size="small">
            <RadioGroup
              row
              value={timeUnit}
              onChange={e => setTimeUnit(e.target.value as 's' | 'h')}
              name="time-unit-toggle"
            >
              <FormControlLabel value="s" control={<Radio size="small" />} label="[s]" />
              <FormControlLabel value="h" control={<Radio size="small" />} label="[h]" />
            </RadioGroup>
          </FormControl>
        </Box>
        {/* Row 7: Empty row for future commands/buttons */}
        <Box sx={{ minHeight: 32 }} />
      </Paper>
    </Box>
  );

  // Central frame: Table
  const centralFrameContent = (
    <Box sx={{ p: 2, width: '100%' }}>
      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table size="small" sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell 
                  key={col.id}
                  sx={{
                    paddingLeft: 0,
                    paddingRight: 0,
                    ...(['exp_id', 'raspi_id', 'exp_status', 'test_id'].includes(col.id)
                      ? { maxWidth: '80px' }
                      : {})
                  }}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleRequestSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                  {/* Only show checklist filter for raspi_id and exp_status */}
                  {['raspi_id', 'exp_status'].includes(col.id) && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 0.5 }}>
                        <Badge 
                          badgeContent={checklistFilters[col.id].length > 0 ? checklistFilters[col.id].length : 0} 
                          color="primary"
                          invisible={checklistFilters[col.id].length === 0}
                        >
                          <IconButton
                            size="small"
                            onClick={e => handleChecklistMenuOpen(col.id, e)}
                            sx={{ padding: '4px' }}
                          >
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                        </Badge>
                      </Box>
                      <Menu
                        anchorEl={anchorEls[col.id]}
                        open={Boolean(anchorEls[col.id])}
                        onClose={() => handleChecklistMenuClose(col.id)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      >
                        <MenuItem disabled>
                          <b>Select {col.label}</b>
                        </MenuItem>
                        <MenuItem onClick={() => handleChecklistClear(col.id)}>
                          <ListItemText primary="Clear All" />
                        </MenuItem>
                        {col.id === 'raspi_id' && uniqueRaspiIds.map(val => {
                          if (typeof val !== 'string' || !val) return null;
                          const filteredChecklistRaspi = Array.isArray(checklistFilters[col.id]) ? checklistFilters[col.id].filter((v): v is string => typeof v === 'string' && !!v) : [];
                          // Defensive: ensure val is string and not null
                          if (typeof val !== 'string' || !val) return null;
                          return (
                            <MenuItem key={val} onClick={() => handleChecklistToggle(col.id, val)}>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={filteredChecklistRaspi.includes(val)}
                                  tabIndex={-1}
                                  disableRipple
                                  size="small"
                                />
                              </ListItemIcon>
                              <ListItemText primary={val} />
                            </MenuItem>
                          );
                        })}
                        {col.id === 'exp_status' && uniqueExpStatuses.map(val => {
                          if (typeof val !== 'string' || !val) return null;
                          const filteredChecklistStatus = Array.isArray(checklistFilters[col.id]) ? checklistFilters[col.id].filter((v): v is string => typeof v === 'string' && !!v) : [];
                          // Defensive: ensure val is string and not null
                          if (typeof val !== 'string' || !val) return null;
                          return (
                            <MenuItem key={val} onClick={() => handleChecklistToggle(col.id, val)}>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={filteredChecklistStatus.includes(val)}
                                  tabIndex={-1}
                                  disableRipple
                                  size="small"
                                />
                              </ListItemIcon>
                              <ListItemText primary={val} />
                            </MenuItem>
                          );
                        })}
                      </Menu>
                    </>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow
                key={row.exp_id}
                hover
                selected={row.exp_id === selectedExpId}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell 
                  onClick={() => handleRowClick(row.exp_id)} 
                  sx={{ 
                    color: 'primary.main', 
                    fontWeight: 'bold', 
                    textDecoration: 'underline',
                    maxWidth: '60px'
                  }}
                >
                  {row.exp_id}
                </TableCell>
                <TableCell sx={{ maxWidth: '60px' }}>
                  {row.raspi_id || '—'}
                </TableCell>
                <TableCell sx={{ maxWidth: '60px' }}>
                  {row.exp_status}
                </TableCell>
                <TableCell sx={{ maxWidth: '60px' }}>
                  {row.test_id}
                </TableCell>
                <TableCell>{formatDate(row.start_time)}</TableCell>
                <TableCell>{formatDate(row.end_time)}</TableCell>
                <TableCell sx={{ padding: '4px 8px' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Devices Progress */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="fieldLabel" sx={{ textAlign: 'right', fontFamily: 'monospace', margin: 0, padding: 0 }}>
                        Devices :
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={row.exp_points_total ? Math.min(Math.max((100 * (row.exp_points_counter || 0)) / row.exp_points_total, 0), 100) : 0}
                        sx={{ width: 120, height: 6, borderRadius: 1 }}
                      />
                      <Typography variant="fieldLabel" sx={{ minWidth: 35 }}>
                        {row.exp_points_counter || 0}/{row.exp_points_total || 0}
                      </Typography>
                    </Box>
                    
                    {/* Expected Progress */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="fieldLabel" sx={{ textAlign: 'right', fontFamily: 'monospace', margin: 0, padding: 0 }}>
                        Expected:
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={row.exp_expect_total ? Math.min(Math.max((100 * (row.exp_expect_counter || 0)) / row.exp_expect_total, 0), 100) : 0}
                        sx={{ width: 120, height: 6, borderRadius: 1 }}
                      />
                      <Typography variant="fieldLabel" sx={{ minWidth: 35 }}>
                        {row.exp_expect_counter || 0}/{row.exp_expect_total || 0}
                      </Typography>
                    </Box>
                    
                    {/* Time Progress */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="fieldLabel" sx={{ textAlign: 'right', fontFamily: 'monospace', margin: 0, padding: 0 }}>
                        Time [s]:
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={row.exp_time_total ? Math.min(Math.max((100 * (row.exp_time_counter || 0)) / row.exp_time_total, 0), 100) : 0}
                        sx={{ width: 120, height: 6, borderRadius: 1 }}
                      />
                      <Typography variant="fieldLabel" sx={{ minWidth: 35 }}>
                        {row.exp_time_counter || 0}/{row.exp_time_total || 0}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {isLoading ? 'Loading…' : 'No experiments found.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );

  // Up frame content with new experiment button
  const upFrameContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        Experiments
      </Typography>
    </Box>
  );

  return (
    <>
      <PageLayout
        upFrameContent={upFrameContent}
        centralFrameContent={centralFrameContent}
        logFrameContent={expDetailsFrame}
        showLogFrame={true}
        isLogFrameExpanded={isDetailsFrameExpanded}
      />

      {/* Experiment Dialog */}
      <ExperimentEditDialog
        open={experimentDialogOpen}
        onClose={() => setExperimentDialogOpen(false)}
        initialExpId={experimentDialogExpId}
        initiatingRaspiId={selectedExperiment?.raspi_id || null}
      />
    </>
  );
};

export default Experiments;
